"use strict";
/**
 * Given the current `editor.children` (old) and a freshly-converted Slate
 * document (dst), this module applies the minimal set of Slate `Transforms`
 * needed to bring the editor in sync with the dst document.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyPatch = void 0;
const tslib_1 = require("tslib");
const slate_1 = require("slate");
const str = tslib_1.__importStar(require("json-joy/lib/util/diff/str"));
const json_equal_1 = require("@jsonjoy.com/json-equal");
/**
 * Apply minimal Slate transforms to make `editor.children` match `dst`.
 */
const applyPatch = (editor, dst) => {
    const oldDoc = editor.children;
    if (oldDoc === dst)
        return;
    slate_1.Editor.withoutNormalizing(editor, () => {
        patchChildren(editor, [], oldDoc, dst);
    });
};
exports.applyPatch = applyPatch;
const patchChildren = (editor, basePath, src, dst) => {
    const srcLen = src.length;
    const dstLen = dst.length;
    const minLen = Math.min(srcLen, dstLen);
    let pfx = 0;
    while (pfx < minLen && (0, json_equal_1.deepEqual)(src[pfx], dst[pfx]))
        pfx++;
    if (pfx === srcLen && pfx === dstLen)
        return;
    let sfx = 0;
    while (sfx < minLen - pfx && (0, json_equal_1.deepEqual)(src[srcLen - 1 - sfx], dst[dstLen - 1 - sfx]))
        sfx++;
    const srcEnd = srcLen - sfx;
    const dstEnd = dstLen - sfx;
    const srcChanged = srcEnd - pfx;
    const dstChanged = dstEnd - pfx;
    const sameCount = srcChanged === dstChanged;
    if (sameCount) {
        for (let i = srcChanged - 1; i >= 0; i--) {
            const idx = pfx + i;
            const oldNode = src[idx];
            const newNode = dst[idx];
            if ((0, json_equal_1.deepEqual)(oldNode, newNode))
                continue;
            patchNode(editor, [...basePath, idx], oldNode, newNode);
        }
    }
    else {
        // Different counts in the changed window.
        // Delete old[pfx..oldEnd) in reverse order, then insert new[pfx..newEnd).
        for (let i = srcEnd - 1; i >= pfx; i--)
            slate_1.Transforms.removeNodes(editor, { at: [...basePath, i] });
        if (dstChanged > 0) {
            const toInsert = dst.slice(pfx, dstEnd);
            slate_1.Transforms.insertNodes(editor, toInsert, { at: [...basePath, pfx] });
        }
    }
};
const attrsEqual = (a, b) => {
    if (a === b)
        return true;
    if (a.type !== b.type)
        return false;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length)
        return false;
    for (const key of aKeys) {
        if (key === 'children')
            continue;
        if (!(0, json_equal_1.deepEqual)(a[key], b[key]))
            return false;
    }
    return true;
};
const patchNode = (editor, path, src, dst) => {
    const srcIsText = 'text' in src;
    const dstIsText = 'text' in dst;
    if (srcIsText && dstIsText) {
        patchTextNode(editor, path, src, dst);
        return;
    }
    // Replace whole node.
    if (srcIsText || dstIsText || src.type !== dst.type) {
        slate_1.Transforms.removeNodes(editor, { at: path });
        slate_1.Transforms.insertNodes(editor, dst, { at: path });
        return;
    }
    // Patch attributes and recurse on children.
    if (!attrsEqual(src, dst)) {
        const { children: _, ...newAttrs } = dst;
        slate_1.Transforms.setNodes(editor, newAttrs, { at: path });
    }
    patchChildren(editor, path, src.children, dst.children);
};
const patchTextNode = (editor, path, src, dst) => {
    // Check whether non-text mark properties changed.
    const oldKeys = Object.keys(src);
    const newKeys = Object.keys(dst);
    const markChanged = oldKeys.length !== newKeys.length || oldKeys.some((k) => k !== 'text' && !(0, json_equal_1.deepEqual)(src[k], dst[k]));
    if (markChanged) {
        slate_1.Transforms.removeNodes(editor, { at: path });
        slate_1.Transforms.insertNodes(editor, dst, { at: path });
        return;
    }
    // Diff and apply text content changes.
    const srcTxt = src.text;
    const dstTxt = dst.text;
    if (srcTxt === dstTxt)
        return;
    str.apply(str.diff(srcTxt, dstTxt), srcTxt.length, (pos, str) => slate_1.Transforms.insertText(editor, str, { at: { path, offset: pos } }), (pos, len) => slate_1.Transforms.delete(editor, { at: { path, offset: pos }, distance: len, unit: 'character' }));
};
